import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { MapPin, Star, Building2, ArrowRight, ShieldCheck, Trophy } from "lucide-react";

export default function DealersPage() {
    const { t } = useTranslation();
    const [dealers, setDealers] = useState([]);

    useEffect(() => {
        (async () => {
            // Fetch all b2b users via listings owners aggregation
            const { data } = await api.get("/listings?limit=200");
            const owners = {};
            for (const l of data) {
                if (l.owner?.role === "b2b") {
                    if (!owners[l.owner.id]) {
                        owners[l.owner.id] = { ...l.owner, listings_count: 0 };
                    }
                    owners[l.owner.id].listings_count += 1;
                }
            }
            setDealers(Object.values(owners));
        })();
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="dealers-page">
            <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2">{t("dealers.kicker")}</div>
                    <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight">{t("dealers.title")}</h1>
                    <p className="text-gray-600 mt-2">{t("dealers.subtitle_count", { count: dealers.length })}</p>
                </div>
                <Link
                    to="/leaderboard"
                    className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] px-4 py-2 rounded-sm font-bold uppercase tracking-wider text-xs border-2 border-[#111827] shrink-0"
                    data-testid="leaderboard-link"
                >
                    <Trophy className="w-4 h-4" /> Top-Händler Ranking
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dealers.map((d) => (
                    <Link
                        key={d.id}
                        to={`/dealer/${d.id}`}
                        className="bg-white border border-gray-200 hover:border-[#111827] hover:shadow-md transition-all p-6 rounded-sm group"
                        data-testid="dealer-card"
                    >
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-16 h-16 bg-[#111827] text-[#FFCC00] flex items-center justify-center rounded-sm font-display font-black text-2xl flex-shrink-0">
                                {(d.company_name || d.name || "?").charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <Badge className="bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm mb-2">{t("dealers.badge_dealer")}</Badge>
                                {d.verified && (
                                    <Badge
                                        className="bg-green-100 text-green-800 hover:bg-green-100 gap-1 rounded-sm uppercase tracking-wider text-[9px] font-bold ml-1"
                                        data-testid="dealer-verified-badge"
                                    >
                                        <ShieldCheck className="w-3 h-3" /> {t("dealer_profile.verified")}
                                    </Badge>
                                )}
                                <div className="font-display font-black text-lg leading-tight truncate">{d.company_name || d.name}</div>
                                {d.rating_count > 0 && (
                                    <div className="flex items-center gap-1 text-sm mt-1" data-testid="dealer-card-rating">
                                        <Star className="w-3.5 h-3.5 fill-[#FFCC00] text-[#FFCC00]" />
                                        <span className="font-mono-num font-bold">{d.rating_avg}</span>
                                        <span className="text-xs text-gray-500">({d.rating_count})</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="space-y-1.5 text-sm text-gray-600 mb-4">
                            {d.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {d.city}</div>}
                            <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-gray-400" /> {t("dealers.listings_count", { count: d.listings_count })}</div>
                        </div>
                        {(d.sell_regional || d.sell_eu || d.sell_international) && (
                            <div className="flex flex-wrap gap-1 mb-3" data-testid={`dealer-reach-${d.id}`}>
                                {d.sell_regional && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 rounded-sm text-[9px] uppercase tracking-wider font-bold">{t("dealers.reach_regional")}</Badge>}
                                {d.sell_eu && <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 rounded-sm text-[9px] uppercase tracking-wider font-bold">{t("dealers.reach_eu")}</Badge>}
                                {d.sell_international && <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 rounded-sm text-[9px] uppercase tracking-wider font-bold">{t("dealers.reach_international")}</Badge>}
                            </div>
                        )}
                        <div className="border-t border-gray-100 pt-3 flex items-center justify-between text-sm font-bold uppercase tracking-wider">
                            <span>{t("dealers.view_profile")}</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
